import type { CartItem } from '~/composables/types/cart'

const ITEM_LIST_ATTRIBUTION_STORAGE_KEY = 'konfetti-gtm-item-list-attribution'
export const CHECKOUT_ITEM_LIST_ATTRIBUTION_STORAGE_KEY =
  'checkout-item-list-attribution'

export interface ItemListAttribution {
  item_list_id: string
  item_list_name: string
}

export type ItemListAttributionMap = Record<string, ItemListAttribution>

const normalizeItemListAttributionKey = (
  id: string | number | undefined | null
): string | null => {
  if (id === undefined || id === null || id === '') return null
  return String(id)
}

const readJsonMap = <T extends Record<string, unknown>>(
  storage: Storage | undefined,
  key: string
): T => {
  if (!storage) return {} as T
  try {
    return JSON.parse(storage.getItem(key) || '{}') as T
  } catch {
    return {} as T
  }
}

const writeJsonMap = (
  storage: Storage | undefined,
  key: string,
  map: Record<string, unknown>
): void => {
  if (!storage || Object.keys(map).length === 0) return
  storage.setItem(key, JSON.stringify(map))
}

export const getItemListAttributionMap = (): ItemListAttributionMap => {
  if (typeof localStorage === 'undefined') return {}
  return readJsonMap<ItemListAttributionMap>(
    localStorage,
    ITEM_LIST_ATTRIBUTION_STORAGE_KEY
  )
}

export const getCheckoutItemListAttributionMap = (): ItemListAttributionMap => {
  if (typeof sessionStorage === 'undefined') return {}
  return readJsonMap<ItemListAttributionMap>(
    sessionStorage,
    CHECKOUT_ITEM_LIST_ATTRIBUTION_STORAGE_KEY
  )
}

export const persistItemListAttribution = (
  itemId: string | number,
  attribution: ItemListAttribution
): void => {
  const key = normalizeItemListAttributionKey(itemId)
  if (!key || typeof localStorage === 'undefined') return

  const map = getItemListAttributionMap()
  map[key] = attribution
  localStorage.setItem(ITEM_LIST_ATTRIBUTION_STORAGE_KEY, JSON.stringify(map))
}

export const persistCheckoutGtmItemListMapsToSessionStorage = (
  items: CartItem[] | undefined
): void => {
  if (typeof sessionStorage === 'undefined' || !items?.length) return

  const attributionMap = getItemListAttributionMap()
  const checkoutMap: ItemListAttributionMap = {}

  items.forEach((item) => {
    const key = normalizeItemListAttributionKey(item.eventDescriptionId)
    if (!key) return
    const attribution = attributionMap[key]
    if (attribution) {
      checkoutMap[key] = attribution
    }
  })

  writeJsonMap(
    sessionStorage,
    CHECKOUT_ITEM_LIST_ATTRIBUTION_STORAGE_KEY,
    checkoutMap
  )
}

export const clearCheckoutItemListAttributionFromSessionStorage = (): void => {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.removeItem(CHECKOUT_ITEM_LIST_ATTRIBUTION_STORAGE_KEY)
}

/**
 * Stamps the "Book Now" A/B bucket onto every GA4 item so GA can segment the
 * experiment: `BUY-NOW` for the variation, `ADD-TO-CART` for control (KFT-3914).
 * A no-op when no category is supplied, so callers stay safe when unset.
 */
export const enrichGtmItemsWithExperiment = <T extends object>(
  items: T[],
  itemCategory4: string | undefined
): (T & { item_category4?: string })[] => {
  if (!itemCategory4) return items
  return items.map((item) => ({ ...item, item_category4: itemCategory4 }))
}

export const enrichGtmItemsWithListAttribution = <
  T extends { item_id: string | number },
>(
  items: T[],
  attributionMap?: ItemListAttributionMap
): T[] => {
  const map = attributionMap ?? getItemListAttributionMap()
  if (Object.keys(map).length === 0) return items

  return items.map((item) => {
    const key = normalizeItemListAttributionKey(item.item_id)
    if (!key) return item
    const attribution = map[key]
    if (!attribution) return item
    return {
      ...item,
      item_list_id: attribution.item_list_id,
      item_list_name: attribution.item_list_name,
    }
  })
}
